import axios from 'axios';

class GitHubService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BACKEND_URL || '';
  }

  // Check if GitHub OAuth is configured
  async isAppConfigured() {
    try {
      // Check if backend is accessible and GitHub OAuth is configured
      const response = await axios.get('/api/github/status');
      return response.data.app_configured === true;
    } catch (error) {
      console.error('Failed to check GitHub OAuth configuration:', error);
      return false;
    }
  }

  // Connect GitHub via OAuth
  async installApp() {
    try {
      // Generate a unique state parameter
      const state = this.generateState();
      
      // Store state in localStorage for verification
      localStorage.setItem('github_oauth_state', state);
      
      // Store the current page URL to return to after connection
      localStorage.setItem('github_oauth_return_url', window.location.href);
      
      // Set connection progress flag in sessionStorage
      sessionStorage.setItem('github_oauth_in_progress', 'true');
      
      // First, get the OAuth authorization URL from our backend
      const response = await axios.get(`/api/github/install?state=${state}`);
      
      if (!response.data.install_url) {
        throw new Error('Failed to get OAuth authorization URL from backend');
      }
      
      // Redirect to the GitHub OAuth URL
      // This will redirect the user to GitHub for authorization
      window.location.href = response.data.install_url;
      
      // Return a promise that resolves when connection is complete
      return new Promise((resolve, reject) => {
        // Since we're redirecting, we'll resolve immediately
        // The actual completion will be handled by the callback component
        resolve({ success: true, message: 'Redirecting to GitHub for authorization' });
      });
      
    } catch (error) {
      console.error('Failed to start GitHub OAuth connection:', error);
      throw error;
    }
  }



  // Generate a unique state parameter
  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }



  // Get GitHub connection status
  async getConnectionStatus() {
    try {
      const response = await axios.get('/api/github/status');
      return response.data;
    } catch (error) {
      console.error('Failed to get GitHub connection status:', error);
      return { connected: false, error: error.message };
    }
  }

  // Create a new repository
  async createRepository(repoData) {
    try {
      const response = await axios.post('/api/github/create-repository', repoData);
      return response.data;
    } catch (error) {
      throw new Error('Failed to create repository: ' + (error.response?.data?.detail || error.message));
    }
  }

  // List user's repositories
  async getUserRepositories() {
    try {
      const response = await axios.get('/api/github/repositories');
      return response.data.repositories;
    } catch (error) {
      throw new Error('Failed to fetch repositories');
    }
  }

  // Get repository commits
  async getRepositoryCommits(repoName) {
    try {
      const response = await axios.get(`/api/github/repositories/${repoName}/commits`);
      return response.data.commits;
    } catch (error) {
      throw new Error('Failed to fetch repository commits');
    }
  }

  // Disconnect GitHub OAuth
  async disconnectGitHubOAuth() {
    try {
      const response = await axios.delete('/api/github/uninstall');
      return response.data;
    } catch (error) {
      console.error('GitHub disconnect error:', error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Failed to disconnect GitHub');
    }
  }

  // Get repository creation form data
  getRepositoryFormData() {
    return {
      name: '',
      description: '',
      private: false,
      auto_init: true,
      gitignore_template: ''
    };
  }

  // Validate repository name
  validateRepositoryName(name) {
    if (!name) return 'Repository name is required';
    if (name.length < 1) return 'Repository name must be at least 1 character';
    if (name.length > 100) return 'Repository name must be less than 100 characters';
    if (!/^[a-zA-Z0-9._-]+$/.test(name)) return 'Repository name can only contain letters, numbers, dots, underscores, and hyphens';
    return null;
  }

  // Get available gitignore templates
  getGitignoreTemplates() {
    return [
      { value: '', label: 'None' },
      { value: 'Node', label: 'Node.js' },
      { value: 'Python', label: 'Python' },
      { value: 'Java', label: 'Java' },
      { value: 'C++', label: 'C++' },
      { value: 'C#', label: 'C#' },
      { value: 'Go', label: 'Go' },
      { value: 'Rust', label: 'Rust' },
      { value: 'PHP', label: 'PHP' },
      { value: 'Ruby', label: 'Ruby' },
      { value: 'Swift', label: 'Swift' },
      { value: 'Kotlin', label: 'Kotlin' },
      { value: 'TypeScript', label: 'TypeScript' },
      { value: 'React', label: 'React' },
      { value: 'Vue', label: 'Vue' },
      { value: 'Angular', label: 'Angular' },
      { value: 'Django', label: 'Django' },
      { value: 'Flask', label: 'Flask' },
      { value: 'Express', label: 'Express.js' },
      { value: 'Laravel', label: 'Laravel' },
      { value: 'Spring', label: 'Spring Boot' },
      { value: 'Docker', label: 'Docker' },
      { value: 'Kubernetes', label: 'Kubernetes' },
      { value: 'Terraform', label: 'Terraform' },
      { value: 'Ansible', label: 'Ansible' }
    ];
  }
}

export default new GitHubService();
